import { Fragment } from 'react';

import type { AdminRunMarker } from '@/api/adminClient';
import {
  MARKER_LABELS,
  eventSummaryChips,
  formatClockTime,
  formatDurationMs,
  formatOffsetMs,
} from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';

const LONG_GAP_MS = 10_000;
const VISIBLE_GAP_MS = 2_000;

const SOURCE_NOTES: Record<AdminRunMarker['source'], string | undefined> = {
  event: undefined,
  derived: 'derived',
  stream: 'from stream',
};

const HIGHLIGHT_KEYS = new Set([
  'plan.approved',
  'agent.editing.start',
  'llm.first_token',
  'agent.first_write_started',
  'agent.first_tool',
]);

/** Gaps only read as "silent" before the agent visibly works: its first finished tool or a file it started writing. */
export function RunPhaseTimeline({ markers }: { markers: AdminRunMarker[] }) {
  if (markers.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
        No key phases recorded for this run.
      </p>
    );
  }

  const visibleWork = markers
    .filter((marker) => marker.key === 'agent.first_tool' || marker.key === 'agent.first_write_started')
    .map((marker) => Date.parse(marker.at));
  const firstToolMs = visibleWork.length > 0 ? Math.min(...visibleWork) : Infinity;

  return (
    <ol className="relative">
      {markers.map((marker, index) => {
        const previous = index > 0 ? markers[index - 1] : undefined;
        const gapMs = previous ? Date.parse(marker.at) - Date.parse(previous.at) : 0;
        const silent = gapMs >= LONG_GAP_MS && Date.parse(marker.at) <= firstToolMs;
        const chips = eventSummaryChips(marker.detail ?? {});
        const highlight = HIGHLIGHT_KEYS.has(marker.key);
        const failed = marker.key === 'run.failed';
        return (
          <Fragment key={`${marker.key}:${marker.at}`}>
            {gapMs >= VISIBLE_GAP_MS ? (
              <li className="grid grid-cols-[4.5rem_1rem_1fr] items-center gap-3 py-1" aria-hidden="true">
                <span />
                <span className="mx-auto h-6 w-px bg-border" />
                <span
                  className={cn('text-xs tabular-nums', silent ? 'font-medium text-warning' : 'text-muted-foreground')}
                >
                  {formatDurationMs(gapMs)} {silent ? 'silent — no key phase' : 'gap'}
                </span>
              </li>
            ) : null}
            <li className="grid grid-cols-[4.5rem_1rem_1fr] items-start gap-3 py-1.5">
              <span className="pt-0.5 text-right font-mono text-xs tabular-nums text-muted-foreground">
                {formatOffsetMs(marker.offsetMs)}
              </span>
              <span className="flex justify-center pt-1.5">
                <span
                  className={cn(
                    'h-2.5 w-2.5 rounded-full ring-4 ring-background',
                    failed ? 'bg-destructive' : highlight ? 'bg-brand' : 'bg-muted-foreground/60',
                  )}
                />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className={cn('text-sm', highlight ? 'font-semibold text-foreground' : 'text-foreground')}>
                    {MARKER_LABELS[marker.key]}
                  </span>
                  <span className="text-xs tabular-nums text-muted-foreground">{formatClockTime(marker.at)}</span>
                  {SOURCE_NOTES[marker.source] ? (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {SOURCE_NOTES[marker.source]}
                    </span>
                  ) : null}
                </div>
                {chips.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {chips.map((chip) => (
                      <span
                        key={chip.key}
                        className="rounded border border-border px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                      >
                        <span className="opacity-70">{chip.key}</span> {chip.value}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
}
